import { useEffect, useMemo, useState } from 'react';
import { AdminCasesPanel } from '~/admin/admin-cases-panel';
import { AdminItemsPanel } from '~/admin/admin-items-panel';
import { AdminLoginForm } from '~/admin/admin-login-form';
import { Button } from '~/components/ui/button';
import {
    assignAdminCaseItem,
    createAdminCase,
    createAdminItem,
    deleteAdminCase,
    deleteAdminItem,
    getAdminCases,
    getAdminItems,
    getAdminMe,
    getCaseItems,
    loginAdmin,
    logoutAdmin,
    unassignAdminCaseItem,
    updateAdminCase,
    updateAdminItem,
    uploadAdminItemImage,
} from '~/lib/api';
import type { AdminItem, AdminUser, CaseItem, CaseSummary } from '~/lib/contracts';
import '~/index.css';

export function AdminApp() {
    const [activeView, setActiveView] = useState<'items' | 'cases'>('items');
    const [user, setUser] = useState<AdminUser | null>(null);
    const [items, setItems] = useState<AdminItem[]>([]);
    const [casesData, setCasesData] = useState<CaseSummary[]>([]);
    const [caseItemsByCaseId, setCaseItemsByCaseId] = useState<Record<number, CaseItem[]>>({});
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [isBusy, setIsBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const refreshItems = async () => {
        const nextItems = await getAdminItems();
        setItems(nextItems);
    };

    const refreshCaseItemsByCaseId = async (targetCases: CaseSummary[]) => {
        const caseItemsEntries = await Promise.all(
            targetCases.map(async (caseData) => {
                const response = await getCaseItems(caseData.id);
                return [caseData.id, response.items] as const;
            }),
        );
        setCaseItemsByCaseId(Object.fromEntries(caseItemsEntries));
    };

    const refreshCases = async () => {
        const nextCases = await getAdminCases();
        setCasesData(nextCases);
        await refreshCaseItemsByCaseId(nextCases);
    };

    const refreshCaseItemsForCurrentCases = async () => {
        await refreshCaseItemsByCaseId(casesData);
    };

    useEffect(() => {
        const loadSession = async () => {
            try {
                const authResponse = await getAdminMe();
                setUser(authResponse.user);
                setErrorMessage(null);
            } catch {
                setUser(null);
            } finally {
                setIsAuthLoading(false);
            }
        };

        void loadSession();
    }, []);

    useEffect(() => {
        if (!user) {
            return;
        }

        const loadData = async () => {
            try {
                setIsBusy(true);
                await Promise.all([refreshItems(), refreshCases()]);
                setErrorMessage(null);
            } catch (error) {
                setErrorMessage(error instanceof Error ? error.message : 'Failed to load admin data');
            } finally {
                setIsBusy(false);
            }
        };

        void loadData();
    }, [user]);

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            setErrorMessage(error.message);
            return;
        }

        setErrorMessage('Unexpected error');
    };

    const withBusy = async (fn: () => Promise<void>) => {
        try {
            setIsBusy(true);
            await fn();
            setErrorMessage(null);
        } catch (error) {
            handleError(error);
        } finally {
            setIsBusy(false);
        }
    };

    const activeCaseName = useMemo(() => {
        const firstCase = casesData[0];
        return firstCase?.name ?? 'No cases yet';
    }, [casesData]);

    const caseNamesByItemId = useMemo(() => {
        const namesByItemId: Record<number, string[]> = {};
        for (const caseData of casesData) {
            const caseItems = caseItemsByCaseId[caseData.id] ?? [];
            for (const caseItem of caseItems) {
                const names = namesByItemId[caseItem.id] ?? [];
                names.push(caseData.name);
                namesByItemId[caseItem.id] = names;
            }
        }

        for (const names of Object.values(namesByItemId)) {
            names.sort((a, b) => a.localeCompare(b));
        }

        return namesByItemId;
    }, [caseItemsByCaseId, casesData]);

    if (isAuthLoading) {
        return (
            <main className="mt-8 flex w-fit flex-col gap-4 mx-auto">
                <p className="text-sm text-white/60">Checking session...</p>
            </main>
        );
    }

    if (!user) {
        return (
            <main className="mt-8 flex w-fit flex-col gap-4 mx-auto">
                <AdminLoginForm
                    isLoading={isBusy}
                    errorMessage={errorMessage}
                    onSubmit={async (username, password) => {
                        await withBusy(async () => {
                            const authResponse = await loginAdmin(username, password);
                            setUser(authResponse.user);
                        });
                    }}
                />
            </main>
        );
    }

    return (
        <main className="mt-8 flex w-5xl self-start flex-col gap-4 mx-auto">
            <section className="rounded-xl border border-orange-500/25 bg-linear-to-br from-zinc-950 to-zinc-900 p-4 shadow-2xl">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h1 className="text-xl md:text-2xl font-bold tracking-wide">Control Room</h1>
                    </div>
                    <Button
                        variant="secondary"
                        disabled={isBusy}
                        onClick={() => {
                            void withBusy(async () => {
                                await logoutAdmin();
                                setUser(null);
                                setActiveView('items');
                                setItems([]);
                                setCasesData([]);
                                setCaseItemsByCaseId({});
                            });
                        }}
                    >
                        Logout
                    </Button>
                </div>
                {errorMessage && <p className="mt-4 text-sm text-red-300">{errorMessage}</p>}
            </section>

            <section className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)] xl:items-start">
                <aside className="rounded-xl border border-orange-500/25 bg-linear-to-br from-zinc-950 to-zinc-900 p-4 shadow-2xl xl:sticky xl:top-5">
                    <h2 className="text-lg font-bold">Views</h2>
                    <div className="mt-3 grid gap-2">
                        <Button
                            variant={activeView === 'items' ? 'default' : 'secondary'}
                            disabled={isBusy}
                            onClick={() => {
                                setActiveView('items');
                            }}
                        >
                            Items
                        </Button>
                        <Button
                            variant={activeView === 'cases' ? 'default' : 'secondary'}
                            disabled={isBusy}
                            onClick={() => {
                                setActiveView('cases');
                            }}
                        >
                            Cases
                        </Button>
                    </div>
                </aside>

                <div className="min-w-0">
                    {activeView === 'items' ? (
                        <AdminItemsPanel
                            items={items}
                            caseNamesByItemId={caseNamesByItemId}
                            isBusy={isBusy}
                            onCreate={async (payload) => {
                                let createdItem: AdminItem | null = null;
                                await withBusy(async () => {
                                    createdItem = await createAdminItem(payload);
                                    await refreshItems();
                                });
                                if (!createdItem) {
                                    throw new Error('Failed to create item');
                                }

                                return createdItem;
                            }}
                            onUpdate={async (itemId, payload) => {
                                await withBusy(async () => {
                                    await updateAdminItem(itemId, payload);
                                    await refreshItems();
                                    await refreshCaseItemsForCurrentCases();
                                });
                            }}
                            onDelete={async (itemId) => {
                                await withBusy(async () => {
                                    await deleteAdminItem(itemId);
                                    await refreshItems();
                                    await refreshCaseItemsForCurrentCases();
                                });
                            }}
                            onUploadImage={async (itemId, file) => {
                                await withBusy(async () => {
                                    await uploadAdminItemImage(itemId, file);
                                    await refreshItems();
                                    await refreshCaseItemsForCurrentCases();
                                });
                            }}
                        />
                    ) : (
                        <AdminCasesPanel
                            casesData={casesData}
                            items={items}
                            caseItemsByCaseId={caseItemsByCaseId}
                            isBusy={isBusy}
                            onCreate={async (payload) => {
                                await withBusy(async () => {
                                    await createAdminCase(payload);
                                    await refreshCases();
                                });
                            }}
                            onUpdate={async (caseId, payload) => {
                                await withBusy(async () => {
                                    await updateAdminCase(caseId, payload);
                                    await refreshCases();
                                });
                            }}
                            onDelete={async (caseId) => {
                                await withBusy(async () => {
                                    await deleteAdminCase(caseId);
                                    await refreshCases();
                                });
                            }}
                            onAssign={async (caseId, itemId, rarity) => {
                                await withBusy(async () => {
                                    await assignAdminCaseItem(caseId, itemId, { rarity });
                                    await refreshCaseItemsForCurrentCases();
                                });
                            }}
                            onUnassign={async (caseId, itemId) => {
                                await withBusy(async () => {
                                    await unassignAdminCaseItem(caseId, itemId);
                                    await refreshCaseItemsForCurrentCases();
                                });
                            }}
                        />
                    )}
                </div>
            </section>
        </main>
    );
}
